import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../framework/BaseWebComponentController';
import TargetsWebComponent from '../component/TargetsWebComponent';
import { TargetsView } from '../view/TargetsView';
import { templateTargets } from '../view/template';
import { DropdownEvents } from '../../ui/gzDropdown/types';
import _ from 'lodash';
import {
  ConsoleRequestSettings,
  EPS_TYPE_EXCLUDED_FOLDER,
  MDM_TYPE_EXCLUDED_FOLDER,
  Node,
  NodesOptionsByServiceTargetId,
  SelectedTargets,
  SERVICE_TARGETS,
  ServiceTargetIds,
  SVE_TYPE_EXCLUDED_FOLDER,
  TargetsViewData,
} from '../type';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { TargetsRepository } from '../repository/TargetsRepository';
import { API_TYPE_EPS, API_TYPE_MDM, API_TYPE_SVE } from '../../webcomponents.constants';
import { DynamicTreeDataSource, NodeConfiguration, NodeData, NodeOptions } from '../../ui/gzTree/type';
import { ProtectedEntityTypes } from '@nirvana/shared/constants';
import { DEFAULT_DATAPROVIDER_CONSOLE_REQUESTS, DEFAULT_SELECT_TARGET_OPTIONS } from '../type/TargetSettings';
import { translateProtectedEntity } from '../helpers/translateProtectedEntity';

export class TargetsController extends BaseWebComponentController {
  public component: TargetsWebComponent;
  public view: TargetsView;
  public viewConstructor: typeof TargetsView;
  public dataRepository: TargetsRepository;
  public dataRepositoryConstructor: typeof TargetsRepository;

  private excludedFoldersType = [EPS_TYPE_EXCLUDED_FOLDER, SVE_TYPE_EXCLUDED_FOLDER, MDM_TYPE_EXCLUDED_FOLDER];
  public optionsForGetChildrenNodes: NodesOptionsByServiceTargetId = DEFAULT_SELECT_TARGET_OPTIONS;
  public consoleRequestSettings: ConsoleRequestSettings = DEFAULT_DATAPROVIDER_CONSOLE_REQUESTS;
  public invalidTargetTypes: number[] = [];
  public showOnlyCompanies = false;
  public showOnlyManagedByAbove: boolean | undefined = false;
  public showExcludedFolder: boolean | undefined = false;
  private userHasRightsForNetworkTargets: boolean | undefined = true;
  private rootNode: NodeOptions;
  private userMode = true;
  private rootNodeId: string;

  constructor(component: TargetsWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: TargetsView,
      viewTemplate: templateTargets,
      dataRepositoryConstructor: TargetsRepository,
    });
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$serviceTargetsDropDown,
        event: DropdownEvents.OPTION_SELECTED,
        callback: this.onServiceChange,
      },
    ];
  }

  public buildDataRepository(): void {
    if (!this.dependencies.api.type) {
      this.dependencies.api.type = API_TYPE_EPS;
    }
    super.buildDataRepository();
  }

  public async getDataForView(): Promise<TargetsViewData | undefined> {
    const viewData = this.component.getViewData();

    if (!viewData?.companyId || !viewData?.availableServices) {
      return;
    }

    this.view.setLoadingState();
    await this.updateAttributesDependingOnViewData(viewData);
    viewData.treeDataSource = await this.getTreeDataSource();
    viewData.rootNode = this.rootNode;
    viewData.isSingleSelect = this.component.getIsSingleSelect();

    return viewData;
  }

  /**
   * Update current attributes depending on received data
   */
  private async updateAttributesDependingOnViewData(viewData: TargetsViewData) {
    this.view.companyId = viewData.companyId;

    // get first available service
    this.view.currentServiceTargetId = viewData.availableServices[0]
      ? viewData.availableServices[0].id
      : ServiceTargetIds.EPS;

    this.invalidTargetTypes = viewData.invalidTargetTypes ?? [];
    this.userHasRightsForNetworkTargets = viewData.userHasRightsForNetworkTargets ?? true;

    if (viewData.hasOwnProperty('showOnlyCompanies')) {
      this.showOnlyCompanies = viewData.showOnlyCompanies ?? false;
      Object.keys(this.optionsForGetChildrenNodes).forEach(serviceId => {
        this.optionsForGetChildrenNodes[serviceId].showOnlyCompanies = viewData.showOnlyCompanies;
      });
    }

    if (viewData.hasOwnProperty('showOnlyManagedByAbove')) {
      this.showOnlyManagedByAbove = viewData.showOnlyManagedByAbove;
      Object.keys(this.optionsForGetChildrenNodes).forEach(serviceId => {
        this.optionsForGetChildrenNodes[serviceId].showOnlyManagedByAbove = viewData.showOnlyManagedByAbove;
      });
    }

    if (viewData.hasOwnProperty('showExcludedFolder')) {
      this.showExcludedFolder = viewData.showExcludedFolder;
      Object.keys(this.optionsForGetChildrenNodes).forEach(serviceId => {
        this.optionsForGetChildrenNodes[serviceId].showExcludedFolder = viewData.showExcludedFolder;
      });
    }

    if (viewData.hasOwnProperty('preSelectedTargets')) {
      viewData.preSelectedTargets?.forEach(preSelectedTarget => {
        preSelectedTarget.targets = preSelectedTarget.targets.flatMap(target => {
          return this.nodeShouldBeVisible(target.protectedEntityId, target.type, target.path) ? target : [];
        });
      });
    }

    if (viewData?.userMode) {
      this.userMode = viewData.userMode;
    }
  }

  private async onServiceChange(event: WebComponentEvent): Promise<void> {
    this.view.setLoadingState();
    try {
      const newServiceId = event.detail.newValue.id;
      const oldServiceId = event.detail.oldValue.id;

      // nothing to do if there is no old value
      if (!oldServiceId) {
        return;
      }

      // nothing to do if user select the same service
      if (newServiceId === oldServiceId) {
        return;
      }

      this.view.currentServiceTargetId = newServiceId;

      const treeDataSource = await this.getTreeDataSource();
      this.view.setTreeData(treeDataSource);

      // select nodes for new service
      this.view.dom.$targetsTree.selectedNodes = this.view.selectedTargets[newServiceId];
      this.view.setSearchListGroups(this.view.selectedTargets[this.view.currentServiceTargetId]);
      this.view.toggleTargetsSelectedError();
    } finally {
      this.view.removeLoadingState();
    }
  }

  private getApiTypeDataProvider(): string {
    switch (this.view.currentServiceTargetId) {
      case SERVICE_TARGETS.SVE.id:
        return API_TYPE_SVE;
      case SERVICE_TARGETS.MDM.id:
        return API_TYPE_MDM;
      default:
        return API_TYPE_EPS;
    }
  }

  private async getTreeDataSource(): Promise<DynamicTreeDataSource> {
    const dependencies = {
      api: {
        type: this.getApiTypeDataProvider(),
      },
      isCloud: this.dependencies.isCloud,
    };

    this.dataRepository = new this.dataRepositoryConstructor();
    this.dataRepository.injectDependencies(dependencies);

    this.rootNode = await this.getRootNode();
    return new DynamicTreeDataSource({
      rootNodeIds: [this.rootNode.currentNode.nodeId],
      dragAndDropEnabled: false,
      cacheEnabled: false,
      checkbox: true,
      singleSelect: this.component.getIsSingleSelect(),
      requestNodeData: async (currentNode: NodeData) => {
        return Promise.resolve({
          currentNode: currentNode,
          childNodes: await this.getChildNodes(currentNode),
        });
      },
      requestRootNodeData: () => {
        return Promise.resolve(this.rootNode);
      },
    });
  }

  private async getRootNode(): Promise<NodeOptions> {
    const root = await this.dataRepository.getRootNode({ companyId: this.view.companyId }, this.consoleRequestSettings);
    this.rootNodeId = root._id;

    const rootNodeData = {
      nodeId: root._id,
      nodeName: translateProtectedEntity(root.type, root.name, this.dependencies.isCloud()),
      originalData: {
        path: root.path,
        type: root.type,
        company: root.company,
      },
      noCheckbox: root?.readOnly ?? false,
      isOpen: true,
      label: this.view.getNodeLabel(root),
    } as NodeData;

    const childNodes = await this.getChildNodes(rootNodeData);
    return {
      currentNode: rootNodeData,
      childNodes: childNodes,
    } as NodeOptions;
  }

  private async getChildNodes(currentNode: NodeData): Promise<Array<NodeConfiguration>> {
    try {
      const childNodes = [] as Array<NodeConfiguration>;
      const options = this.optionsForGetChildrenNodes[this.view.currentServiceTargetId];
      const nodeChildren = await this.dataRepository.getChildNodes(
        {
          node: currentNode.nodeId,
          viewType: this.getViewType(currentNode.originalData.type),
          options: options,
        },
        this.consoleRequestSettings
      );

      for (const child of nodeChildren.data) {
        if (!this.nodeShouldBeVisible(child._id, child.type, child.path)) {
          continue;
        }

        childNodes.push({
          nodeId: child._id,
          nodeName: translateProtectedEntity(child.type, child.name, this.dependencies.isCloud()),
          originalData: {
            path: child.path,
            type: child.type,
            company: child.company,
          },
          noCheckbox: child?.readOnly ?? false,
          label: this.view.getNodeLabel(child),
          leaf: this.isLeafNode(child),
        } as NodeConfiguration);
      }
      return childNodes;
    } catch (e) {
      return [];
    }
  }

  private getViewType(type: number): number {
    switch (this.view.currentServiceTargetId) {
      case ServiceTargetIds.SVE:
      case ServiceTargetIds.EPS:
        return this.dataRepository.getViewType(type);
      case ServiceTargetIds.MDM:
        return this.dependencies.api.clients.MDM.getViewType({ type: type });
      default:
        return this.dataRepository.getViewType(type);
    }
  }

  private nodeShouldBeVisible(nodeId: string, nodeType: number, nodePath: string): boolean {
    if (this.excludedFoldersType.includes(nodeType)) {
      return false;
    }

    if (this.invalidTargetTypes.includes(nodeType)) {
      return false;
    }

    // filter network items
    if (
      !this.userHasRightsForNetworkTargets &&
      nodeType !== ProtectedEntityTypes.COMPANIES_COLLECTION &&
      nodeType !== ProtectedEntityTypes.COMPANY_FOLDER &&
      nodeType !== ProtectedEntityTypes.COMPANY
    ) {
      return false;
    }

    // filter items that are not descendants of company
    return (nodePath + nodeId + '/').match('/' + this.view.companyId + '/') != null;
  }

  private isLeafNode(node: Node): boolean {
    return this.dataRepository.isLeafNode(node, this.userMode, this.showOnlyCompanies, this.rootNodeId);
  }

  public convertProtectedEntityToServiceTargets(): SelectedTargets {
    const serviceTargets: SelectedTargets = [];

    const validTargetKeys = ['company', 'name', 'path', 'protectedEntityId', 'type'];
    const selectedTargets = this.view.selectedTargets;
    Object.keys(selectedTargets).forEach(key => {
      if (!isEmpty(selectedTargets[key])) {
        // remove unused params
        const sanitizedTargets = selectedTargets[key].map(a => {
          a.value.protectedEntityId = a.value.nodeId;
          a.value.name = a.value.nodeName;
          a.value.path = a.value.originalData.path;
          a.value.type = a.value.originalData.type;
          a.value.company = a.value.originalData.company;
          const cleanTarget = _.cloneDeep(a.value);

          Object.keys(cleanTarget).forEach(target => {
            return validTargetKeys.includes(target) || delete cleanTarget[target];
          });
          return cleanTarget;
        });
        const elem = {
          serviceId: Number(key),
          targets: sanitizedTargets,
        };

        serviceTargets.push(elem);
      }
    });

    return serviceTargets;
  }
}
